import '../styles/Contact/Contact.css';
import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import emailjs from '@emailjs/browser';

const inquiryTypes = [
  { value: '', label: 'Select inquiry type' },
  { value: 'general', label: 'General Inquiry' },
  { value: 'advisory', label: 'Advisory Services' },
  { value: 'research', label: 'Research Collaboration' },
  { value: 'media', label: 'Media & Press' },
  { value: 'careers', label: 'Careers' },
  { value: 'partnership', label: 'Partnership' },
  { value: 'request brief', label: 'Request Brief' },
];

// EmailJS config
const EMAILJS_SERVICE_ID = 'd9c0c879-39df-429b-b09c-';
const EMAILJS_PUBLIC_KEY = 'EGGibSqyznZFGk-k5';
const TEMPLATE_NOTIFICATION = 'template_2tphyws';   // To you (info@actyra.co.ke)
const TEMPLATE_AUTOREPLY = 'template_r0r6d5h';       // To user ({{email}})

export default function Contact() {
  const formRef = useRef();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    inquiryType: '',
    message: '',
    newsletter: false,
    privacy: false,
  });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    if (!formData.privacy) {
      setStatus({
        type: 'error',
        message: 'Please agree to the privacy policy to proceed.',
      });
      setIsSubmitting(false);
      return;
    }

    try {
      // 1. Send notification to YOU
      await emailjs.sendForm(
        EMAILJS_SERVICE_ID,
        TEMPLATE_NOTIFICATION,
        formRef.current,
        EMAILJS_PUBLIC_KEY
      );

      // 2. Send auto-reply to USER
      await emailjs.sendForm(
        EMAILJS_SERVICE_ID,
        TEMPLATE_AUTOREPLY,
        formRef.current,
        EMAILJS_PUBLIC_KEY
      );

      setStatus({
        type: 'success',
        message: 'Your message has been sent. We\'ll get back to you within 24 hours.',
      });

      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        company: '',
        inquiryType: '',
        message: '',
        newsletter: false,
        privacy: false,
      });
    } catch (error) {
      console.error('EmailJS error:', error);
      setStatus({
        type: 'error',
        message: 'Something went wrong. Please try again or email us directly at info@actyra.co.ke',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-page">
      {/* Back to Home */}
      <Link to="/" className="back-home-btn">
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M19 12H5M12 19l-7-7 7-7" />
        </svg>
        Back Home
      </Link>

      {/* Hero */}
      <section className="contact-hero">
        <div className="contact-hero-left">
          <div className="hero-eyebrow">
            <span className="hero-eyebrow-dot" />
            Get in Touch
          </div>
          <h1>
            Let's Start a<br />
            <span className="accent">Conversation.</span>
          </h1>
          <p className="hero-desc">
            Whether you're seeking actuarial expertise, research collaboration,
            or simply want to learn more about our work — we'd love to hear from you.
            Our team typically responds within 24 hours.
          </p>
        </div>

        <div className="contact-hero-right">
          <a href="mailto:info@actyra.co.ke" className="contact-card-large">
            <div className="card-icon-wrap">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                <polyline points="22,6 12,13 2,6" />
              </svg>
            </div>
            <div className="card-content">
              <h3>Email Us</h3>
              <p>info@actyra.co.ke</p>
            </div>
            <div className="card-arrow">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M7 17L17 7M17 7H7M17 7v10" />
              </svg>
            </div>
          </a>

          <a href="tel:+254 740 095 045" className="contact-card-large">
            <div className="card-icon-wrap">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" />
              </svg>
            </div>
            <div className="card-content">
              <h3>Call Us</h3>
              <p>+254 740 095 045</p>
            </div>
            <div className="card-arrow">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M7 17L17 7M17 7H7M17 7v10" />
              </svg>
            </div>
          </a>

          <div className="contact-card-large" style={{ cursor: 'default' }}>
            <div className="card-icon-wrap">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
            </div>
            <div className="card-content">
              <h3>Visit Us</h3>
              <p>Nairobi, Kenya</p>
            </div>
          </div>
        </div>
      </section>

      <div className="section-divider" />

      {/* Main — Form + Sidebar */}
      <section className="contact-main">
        {/* Form */}
        <div className="contact-form-section">
          <div className="form-section-header">
            <div className="form-section-tag">Send a Message</div>
            <h2>How Can We Help?</h2>
            <p>
              Fill out the form and we'll respond as soon as possible.
              Fields marked with <span style={{ color: 'var(--crimson)' }}>*</span> are required.
            </p>
          </div>

          {status.message && (
            <div className={`form-status ${status.type}`} role="alert">
              {status.type === 'success' ? (
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
              )}
              {status.message}
            </div>
          )}

          <form ref={formRef} onSubmit={handleSubmit} className="contact-form" noValidate>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label" htmlFor="firstName">
                  First Name <span className="form-required">*</span>
                </label>
                <input
                  id="firstName"
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Ngoma"
                  required
                  autoComplete="given-name"
                />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="lastName">
                  Last Name <span className="form-required">*</span>
                </label>
                <input
                  id="lastName"
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Allan"
                  required
                  autoComplete="family-name"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label" htmlFor="email">
                  Email <span className="form-required">*</span>
                </label>
                <input
                  id="email"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="ngoma@company.com"
                  required
                  autoComplete="email"
                />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="phone">Phone</label>
                <input
                  id="phone"
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="+254 740 095 045"
                  autoComplete="tel"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label" htmlFor="company">Company / Organization</label>
                <input
                  id="company"
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Acme Insurance Ltd"
                  autoComplete="organization"
                />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="inquiryType">
                  Inquiry Type <span className="form-required">*</span>
                </label>
                <select
                  id="inquiryType"
                  name="inquiryType"
                  value={formData.inquiryType}
                  onChange={handleChange}
                  className="form-select"
                  required
                >
                  {inquiryTypes.map(type => (
                    <option key={type.value} value={type.value} disabled={type.value === ''}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="message">
                Message <span className="form-required">*</span>
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                className="form-textarea"
                placeholder="Tell us about your project, question, or how we can help..."
                required
              />
            </div>

            <div className="form-checkbox-group">
              <label className="form-checkbox-label">
                <input
                  type="checkbox"
                  name="newsletter"
                  checked={formData.newsletter}
                  onChange={handleChange}
                  className="form-checkbox"
                />
                Subscribe to our actuarial insights newsletter
              </label>
              <label className="form-checkbox-label">
                <input
                  type="checkbox"
                  name="privacy"
                  checked={formData.privacy}
                  onChange={handleChange}
                  className="form-checkbox"
                />
                I agree to the{' '}
                <Link to="/privacy" style={{ color: 'var(--teal)', textDecoration: 'none', fontWeight: 600 }}>
                  Privacy Policy
                </Link>{' '}
                and processing of my personal data{' '}
                <span className="form-required">*</span>
              </label>
            </div>

            <button type="submit" className="form-submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <svg viewBox="0 0 24 24" aria-hidden="true" className="spin">
                    <path d="M12 2v4m0 12v4M4.93 4.93l2.83 2.83m8.48 8.48l2.83 2.83M2 12h4m12 0h4M4.93 19.07l2.83-2.83m8.48-8.48l2.83-2.83" />
                  </svg>
                  <span>Sending…</span>
                </>
              ) : (
                <>
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <line x1="22" y1="2" x2="11" y2="13" />
                    <polygon points="22 2 15 22 11 13 2 9 22 2" />
                  </svg>
                  <span>Send Message</span>
                </>
              )}
            </button>
          </form>
        </div>

        {/* Sidebar */}
        <aside className="contact-sidebar" aria-label="Contact information">
          <div className="sidebar-glass">
            <div className="sidebar-header">
              <div className="sidebar-header-tag">Contact Details</div>
              <h3>How to Reach Us</h3>
              <p>Our team is ready to assist you</p>
            </div>

            <div className="sidebar-body">
              <div className="sidebar-detail">
                <div className="sidebar-detail-icon">
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                    <polyline points="22,6 12,13 2,6" />
                  </svg>
                </div>
                <div className="sidebar-detail-content">
                  <h4>Email</h4>
                  <a href="mailto:info@actyra.co.ke">info@actyra.co.ke</a>
                </div>
              </div>

              <div className="sidebar-detail">
                <div className="sidebar-detail-icon">
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" />
                  </svg>
                </div>
                <div className="sidebar-detail-content">
                  <h4>Phone</h4>
                  <a href="tel:+254 740 095 045">+254 740 095 045</a>
                </div>
              </div>

              <div className="sidebar-detail">
                <div className="sidebar-detail-icon">
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                </div>
                <div className="sidebar-detail-content">
                  <h4>Office</h4>
                  <p>Nairobi, Kenya<br />East Africa Time (EAT)</p>
                </div>
              </div>

              <div className="sidebar-detail">
                <div className="sidebar-detail-icon">
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12 6 12 12 16 14" />
                  </svg>
                </div>
                <div className="sidebar-detail-content">
                  <h4>Response Time</h4>
                  <p>Within 24 hours<br />Mon — Fri, 8am — 6pm EAT</p>
                </div>
              </div>
            </div>

            {/* Updated Social Links — matching Footer exactly */}
            <div className="sidebar-social">
              <a 
                href="https://www.linkedin.com/company/actyr%C7%8E/?viewAsMember=true" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="social-link" 
                aria-label="LinkedIn"
              >
                <i className="ti ti-brand-linkedin" />
              </a>
              <a 
                href="https://x.com/actyralimited" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="social-link" 
                aria-label="Twitter / X"
              >
                <i className="ti ti-brand-x" />
              </a>
              <a 
                href="https://www.instagram.com/actyralimited/" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="social-link" 
                aria-label="Instagram"
              >
                <i className="ti ti-brand-instagram" />
              </a>
              <a 
                href="https://web.facebook.com/profile.php?id=61577411862890" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="social-link" 
                aria-label="Facebook"
              >
                <i className="ti ti-brand-facebook" />
              </a>
              <a 
                href="mailto:info@actyra.co.ke" 
                className="social-link" 
                aria-label="Email"
              >
                <i className="ti ti-mail" />
              </a>
            </div>
          </div>
        </aside>
      </section>

      {/* Trust Bar */}
      <section className="contact-trust-bar" aria-label="Company statistics">
        <div className="trust-bar-content">
          <div className="trust-item">
            <div className="trust-item-number"><span>24</span>h</div>
            <div className="trust-item-label">Response Time</div>
          </div>
          <div className="trust-item">
            <div className="trust-item-number"><span>54</span>+</div>
            <div className="trust-item-label">African Markets</div>
          </div>
          <div className="trust-item">
            <div className="trust-item-number"><span>5</span></div>
            <div className="trust-item-label">Research Pillars</div>
          </div>
          <div className="trust-item">
            <div className="trust-item-number"><span>100</span>%</div>
            <div className="trust-item-label">Independent</div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <div className="contact-footer">
        <p>
          Prefer email? Reach us directly at{' '}
          <a href="mailto:info@actyra.co.ke">info@actyra.co.ke</a>
          {' '}or call{' '}
          <a href="tel:+254740095045">+254 740 095 045</a>.
          We typically respond within 24 hours during business days.
        </p>
      </div>
    </div>
  );
}
